import { useEffect, useMemo, useState } from 'react';
import { airportOptionOf, type AirportNaming, type AirportSource } from './airport_option.js';
import type { SearchSelectOption } from './place_option.js';
import type { PlannerRecentAirports } from './planner_models.js';

/** O histórico de aeroportos do viajante, como o host o lê. Estrutural: o port
 *  `flightAirportHistory` da conversa e o fetch da página cabem nele. */
export interface AirportHistorySource {
  getAirportHistory: () => Promise<{
    origin: readonly AirportSource[];
    destination: readonly AirportSource[];
  }>;
}

type AirportHistory = Awaited<ReturnType<AirportHistorySource['getAirportHistory']>>;

/** Constante de módulo (não literal no `useState`): identidade estável é o que
 *  impede o `sections` do combobox de trocar de referência a cada render. */
const NONE: PlannerRecentAirports = { origin: [], destination: [] };

/** A MESMA opção da busca por texto, com o glifo trocado pelo relógio: dentro de
 *  Recentes a procedência ("você já buscou isto") pesa mais que o tipo — que
 *  segue legível no código e na legenda (chamada do designer, 2026-08-19; o
 *  mesmo tratamento do grupo Recentes do `Onde` no hotel). */
function recentOptionOf(a: AirportSource, naming: AirportNaming): SearchSelectOption {
  const option = airportOptionOf(a, naming);
  return option.detail ? { ...option, detail: { ...option.detail, icon: 'history' } } : option;
}

/**
 * **O histórico de aeroportos do viajante, na forma que o combobox DE/PARA consome**
 * (a prop `recentAirports` do `PlannerForm`).
 *
 * Lê o histórico UMA vez por montagem do host e mapeia os dois conjuntos com o
 * mesmo `airportOptionOf` da busca por texto (via `recentOptionOf`, que só troca o
 * glifo) — o campo não aprende um segundo formato de linha, só uma segunda
 * procedência.
 *
 * O par origem/destino é o do backend, e o mapeamento pro campo é literal: o campo DE
 * consome `origin`, o campo PARA consome `destination`. Não há uma terceira lista
 * ("mais buscados") porque não há um terceiro conjunto no fio — inventar um ranking
 * aqui seria afirmar uma popularidade que ninguém mediu.
 *
 * O `source` PRECISA de identidade estável — é dep do effect, e um objeto novo por
 * render refaria o read. O `naming` só refaz o mapeamento, não o read. Sem
 * `source` (preview, Storybook, host que não injeta) ou com as duas listas vazias,
 * devolve `NONE` e o campo segue só com a busca remota por texto.
 * Falha do read é engolida de propósito — o endpoint já é fail-open, e um histórico
 * ausente não é um erro que a pessoa possa consertar.
 */
export function useRecentAirports(
  source: AirportHistorySource | null,
  naming: AirportNaming,
): PlannerRecentAirports {
  // The result is tagged with the source that produced it: when the host swaps the
  // source (or drops it to `null`), the previous history must not linger while the
  // new read is pending or after it rejects.
  const [loaded, setLoaded] = useState<{
    source: AirportHistorySource;
    history: AirportHistory;
  } | null>(null);
  useEffect(() => {
    if (!source) return undefined;
    let alive = true;
    source
      .getAirportHistory()
      .then((history) => {
        if (alive) setLoaded({ source, history });
      })
      .catch(() => {
        // Degrade silencioso: o campo volta a ser só busca por texto.
      });
    return () => {
      alive = false;
    };
  }, [source]);
  const history = source && loaded?.source === source ? loaded.history : null;
  return useMemo(
    () =>
      history
        ? {
            origin: history.origin.map((a) => recentOptionOf(a, naming)),
            destination: history.destination.map((a) => recentOptionOf(a, naming)),
          }
        : NONE,
    [history, naming],
  );
}
